'use client';

import Image from 'next/image';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import { Box, Grid, Stack, Typography } from '@/primitives';
import { blue, slate } from '@/lib/colours';
import { FadeInWhenVisible } from './FadeInWhenVisible';
import { landingDeepRows, landingDeepSection } from './homeLandingContent';
import { lightSectionBg, sectionShell } from './homeLandingSectionSx';

export function LandingAlternatingFeatures() {
  return (
    <Box
      component="section"
      aria-labelledby="landing-deep-title"
      sx={{
        py: { xs: 8, md: 10 },
        ...lightSectionBg,
      }}
    >
      <Box sx={sectionShell}>
        <FadeInWhenVisible>
          <Stack
            alignItems="center"
            spacing={1}
            sx={{ mb: { xs: 5, md: 7 }, textAlign: 'center', width: '100%' }}
          >
            <Typography
              color={blue[700]}
              sx={{
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.75rem',
                textAlign: 'center',
              }}
              variant="labelmedium"
            >
              {landingDeepSection.eyebrow}
            </Typography>
            <Typography
              color={slate[900]}
              component="h2"
              id="landing-deep-title"
              sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center' }}
              variant="headlinelarge"
            >
              {landingDeepSection.title}
            </Typography>
            <Typography
              color={slate[600]}
              sx={{ maxWidth: 560, mx: 'auto', mt: 1, textAlign: 'center' }}
              variant="bodylarge"
            >
              {landingDeepSection.subtitle}
            </Typography>
          </Stack>
        </FadeInWhenVisible>

        <Stack spacing={{ xs: 6, md: 8 }}>
          {landingDeepRows.map((row, index) => {
            const isEven = index % 2 === 0;
            const Icon =
              row.id === 'search'
                ? SearchOutlinedIcon
                : SettingsSuggestOutlinedIcon;
            return (
              <FadeInWhenVisible key={row.id} delay={index * 0.06}>
                <Grid
                  alignItems="center"
                  container
                  spacing={{ xs: 3, md: 5 }}
                  direction={{
                    xs: 'column-reverse',
                    md: isEven ? 'row' : 'row-reverse',
                  }}
                >
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack spacing={2}>
                      <Stack alignItems="center" direction="row" spacing={1.5}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: blue[50],
                            color: blue[700],
                          }}
                        >
                          <Icon />
                        </Box>
                        <Typography color={blue[700]} variant="titlemedium">
                          {row.eyebrow}
                        </Typography>
                      </Stack>
                      <Typography color={slate[900]} variant="headlinemedium">
                        {row.title}
                      </Typography>
                      <Typography color={slate[600]} variant="bodylarge">
                        {row.description}
                      </Typography>
                      <Stack
                        component="ul"
                        spacing={1.25}
                        sx={{ pl: 0, listStyle: 'none', m: 0 }}
                      >
                        {row.bullets.map((bullet) => (
                          <Stack
                            key={bullet}
                            alignItems="flex-start"
                            component="li"
                            direction="row"
                            spacing={1.25}
                          >
                            <CheckCircleOutlineIcon
                              sx={{
                                color: blue[600],
                                fontSize: 22,
                                mt: 0.25,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              color={slate[700]}
                              component="span"
                              variant="bodymedium"
                            >
                              {bullet}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    {row.id === 'registry' ? (
                      <Image
                        alt=""
                        height={626}
                        sizes="(max-width: 900px) 100vw, 560px"
                        src="/images/registry-hero-2.png"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'contain',
                        }}
                        width={1024}
                      />
                    ) : (
                      <Image
                        alt=""
                        height={626}
                        sizes="(max-width: 900px) 100vw, 560px"
                        src="/images/registry-hero-ipad.png"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'contain',
                        }}
                        width={1024}
                      />
                    )}
                  </Grid>
                </Grid>
              </FadeInWhenVisible>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
